import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { formatTicketPrice, formatDiscountPreview, slugifyRef } from './paymentFormUtils';
import { recalculatePromoPrice } from './linkEditUtils';

export interface EditDiscountLinkValues {
  label: string;
  refCode: string;
  discountPercent?: number;
}

interface EditDiscountLinkDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: EditDiscountLinkValues) => void;
  onDelete?: () => void;
  hasDiscount?: boolean;
  initialLabel: string;
  initialRefCode: string;
  initialDiscountPercent?: number;
  baseTicketName?: string;
  basePrice?: number | null;
}

const EditDiscountLinkDialog: React.FC<EditDiscountLinkDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  hasDiscount = true,
  initialLabel,
  initialRefCode,
  initialDiscountPercent = 10,
  baseTicketName,
  basePrice,
}) => {
  const resolvedBasePrice = basePrice ?? null;
  const [label, setLabel] = useState(initialLabel);
  const [refCode, setRefCode] = useState(initialRefCode);
  const [discountPercent, setDiscountPercent] = useState(String(initialDiscountPercent || ''));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLabel(initialLabel);
    setRefCode(initialRefCode);
    setDiscountPercent(String(initialDiscountPercent || ''));
    setError(null);
  }, [open, initialLabel, initialRefCode, initialDiscountPercent]);

  const discountNum = Math.min(100, Math.max(0, Number(discountPercent) || 0));
  const finalPrice = hasDiscount && resolvedBasePrice != null
    ? recalculatePromoPrice(resolvedBasePrice, discountNum)
    : resolvedBasePrice ?? 0;
  const discountPreview = formatDiscountPreview(resolvedBasePrice ?? 0, discountNum, finalPrice);
  const discountBlocked = hasDiscount && resolvedBasePrice == null;
  const isFreeBase = hasDiscount && resolvedBasePrice === 0;

  const handleSave = () => {
    if (!label.trim()) {
      setError('Informe um nome para o link.');
      return;
    }
    if (!refCode.trim()) {
      setError('Informe o código ref.');
      return;
    }
    if (hasDiscount && resolvedBasePrice == null) {
      onSave({
        label: label.trim(),
        refCode: slugifyRef(refCode),
      });
      onClose();
      return;
    }
    if (hasDiscount && resolvedBasePrice === 0) {
      onSave({
        label: label.trim(),
        refCode: slugifyRef(refCode),
      });
      onClose();
      return;
    }
    if (hasDiscount) {
      if (discountNum <= 0 || discountNum >= 100) {
        setError('O desconto deve ser entre 1% e 99%.');
        return;
      }
      if (finalPrice <= 0) {
        setError('Preço com desconto inválido.');
        return;
      }
    }

    onSave({
      label: label.trim(),
      refCode: slugifyRef(refCode),
      ...(hasDiscount ? { discountPercent: discountNum } : {}),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{hasDiscount ? 'Editar link com desconto' : 'Editar link'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {hasDiscount && resolvedBasePrice != null && resolvedBasePrice > 0 && baseTicketName && (
            <Typography variant="body2" color="text.secondary">
              Ingresso base: <strong>{baseTicketName}</strong> ({formatTicketPrice(resolvedBasePrice)})
            </Typography>
          )}

          <TextField
            label="Nome do link"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
            helperText="Só para você se organizar — aparece na lista de links."
          />

          <TextField
            label="Código na URL (ref)"
            value={refCode}
            onChange={(e) => setRefCode(e.target.value)}
            fullWidth
            size="small"
            helperText="Quem já recebeu o link antigo precisará do novo se você mudar o código."
          />

          {hasDiscount && (
            <>
              {discountBlocked ? (
                <Alert severity="warning">
                  Não foi possível identificar o preço do ingresso base. Confira o valor na aba
                  Ingressos e salve o evento — ou edite só o nome, o código ref ou exclua o link.
                </Alert>
              ) : isFreeBase ? (
                <Alert severity="warning">
                  O ingresso base está <strong>gratuito</strong>. Desconto não altera o valor — você
                  pode alterar o nome, o código ref ou excluir o link.
                </Alert>
              ) : (
                <>
                  <TextField
                    label="Desconto"
                    type="number"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    size="small"
                    sx={{ maxWidth: 160 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    inputProps={{ min: 1, max: 99 }}
                  />

                  <Alert severity="info">
                    Novo valor cobrado: <strong>{discountPreview.headline}</strong>
                    {discountPreview.detail && <> — {discountPreview.detail}</>}
                  </Alert>

                  <Typography variant="caption" color="text.secondary">
                    Ao salvar, o preço do ingresso oculto deste link é atualizado. O ingresso público não muda.
                  </Typography>
                </>
              )}
            </>
          )}

          {!hasDiscount && (
            <Typography variant="caption" color="text.secondary">
              Este link usa o preço normal do ingresso — se você alterar o preço na aba Ingressos, quem usar o link verá o valor atualizado.
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button color="error" onClick={onDelete} disabled={!onDelete}>
          Excluir link
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            Salvar alterações
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default EditDiscountLinkDialog;
